import React from 'react';
import { Link } from '../../atoms/Link';
import { Image } from '../../atoms/Image';
import { Text } from '../../atoms/Text';
import type { AssetImport } from '../../helpers/assetSrc';
import crossWhiteSvg from '../../assets/svg/cross-white.svg';

export interface ResumePurchaseBoxProps {
  /**
   * Resume purchase URL
   */
  resumeUrl: string;
  /**
   * Title text
   */
  title?: string;
  /**
   * Body message text
   */
  message?: string;
  /**
   * Resume button text
   */
  resumeText?: string;
  /**
   * Dismiss button text
   */
  dismissText?: string;
  /**
   * Resume button icon. Accepts a URL string (webpack/UMD consumers) or a
   * static-import object (Next/Turbopack consumers). Normalisation happens
   * inside the Image atom.
   */
  resumeIcon?: AssetImport;
  /**
   * Dismiss button icon. Same shape as `resumeIcon`.
   */
  dismissIcon?: AssetImport;
  /**
   * Close handler
   */
  onClose?: () => void;
  /**
   * Dismiss handler (when user clicks "Don't remind me")
   */
  onDismiss?: () => void;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Visible state
   */
  visible?: boolean;
}

export const ResumePurchaseBox: React.FC<ResumePurchaseBoxProps> = ({
  resumeUrl,
  title = 'Continue checkout?',
  message = "We noticed you didn't complete your order, would you like to resume from where you left?",
  resumeText = 'Continue with order',
  dismissText = "Don't remind me",
  resumeIcon,
  dismissIcon,
  onClose,
  onDismiss,
  className = '',
  visible = true,
}) => {
  if (!visible) {
    return null;
  }

  const baseClass = 'hi-ui hi-ui-resume-purchase-box';
  const allClasses = `${baseClass} ${className}`.trim();

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (onClose) {
      onClose();
    }
  };

  const handleDismiss = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <div className={allClasses} id="sideresume">
      <div className="hi-ui-resume-purchase-close">
        <button
          type="button"
          title="Hide for this session"
          onClick={handleClose}
          className="hi-ui-resume-purchase-close-link"
          aria-label="Close"
        >
          <Image src={crossWhiteSvg} alt="Close" width={20} height={20} loading="eager" />
        </button>
      </div>
      <Text className="hi-ui-resume-purchase-title">{title}</Text>
      <Text className="hi-ui-resume-purchase-message">{message}</Text>
      <div className="hi-ui-resume-purchase-actions">
        <Link
          href={resumeUrl}
          className="hi-ui-resume-purchase-button hi-ui-resume-purchase-button-resume"
          noUnderline
        >
          {resumeIcon && (
            <Image src={resumeIcon} alt="" className="hi-ui-resume-purchase-icon" loading="eager" />
          )}
          <Text variant="inline">{resumeText}</Text>
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="hi-ui-resume-purchase-button hi-ui-resume-purchase-button-dismiss"
        >
          {dismissIcon && (
            <Image
              src={dismissIcon}
              alt=""
              className="hi-ui-resume-purchase-icon"
              loading="eager"
            />
          )}
          <Text variant="inline">{dismissText}</Text>
        </button>
      </div>
    </div>
  );
};
